import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FinancialDetailsTable = () => {
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');

  const financialData = [
    {
      id: 1,
      date: '2024-10-27',
      type: 'Commission Revenue',
      vertical: 'Food Delivery',
      amount: 45670,
      status: 'completed',
      disputes: 0,
      payoutDate: '2024-10-29',
      partner: 'Golden Spoon Restaurant',
      orders: 340
    },
    {
      id: 2,
      date: '2024-10-27',
      type: 'Subscription Fee',
      vertical: 'Home Services',
      amount: 12500,
      status: 'completed',
      disputes: 0,
      payoutDate: '2024-10-29',
      partner: 'Elite Home Services',
      orders: 1
    },
    {
      id: 3,
      date: '2024-10-26',
      type: 'Commission Revenue',
      vertical: 'Property',
      amount: 89420,
      status: 'pending',
      disputes: 1,
      payoutDate: '2024-10-30',
      partner: 'Prime Properties Ltd',
      orders: 23
    },
    {
      id: 4,
      date: '2024-10-26',
      type: 'Listing Fee',
      vertical: 'Tours & Travel',
      amount: 8900,
      status: 'completed',
      disputes: 0,
      payoutDate: '2024-10-28',
      partner: 'Adventure Tours Co',
      orders: 1
    },
    {
      id: 5,
      date: '2024-10-25',
      type: 'Commission Revenue',
      vertical: 'Food Delivery',
      amount: 34560,
      status: 'disputed',
      disputes: 2,
      payoutDate: 'On Hold',
      partner: 'Urban Eats Chain',
      orders: 280
    },
    {
      id: 6,
      date: '2024-10-25',
      type: 'Platform Fee',
      vertical: 'Home Services',
      amount: 15670,
      status: 'completed',
      disputes: 0,
      payoutDate: '2024-10-27',
      partner: 'Quick Fix Solutions',
      orders: 45
    }
  ];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-success/10 text-success',
      pending: 'bg-warning/10 text-warning',
      disputed: 'bg-error/10 text-error'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getTypeColor = (type) => {
    const colors = {
      'Commission Revenue': 'bg-blue-100 text-blue-800',
      'Subscription Fee': 'bg-green-100 text-green-800',
      'Listing Fee': 'bg-orange-100 text-orange-800',
      'Platform Fee': 'bg-purple-100 text-purple-800'
    };
    return colors?.[type] || 'bg-gray-100 text-gray-800';
  };

  const filteredData = financialData?.filter(item => 
    filterStatus === 'all' || item?.status === filterStatus
  );

  const sortedData = [...filteredData]?.sort((a, b) => {
    let aVal = a?.[sortField];
    let bVal = b?.[sortField];
    
    if (sortField === 'amount') {
      aVal = parseFloat(aVal);
      bVal = parseFloat(bVal);
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  return (
    <div className="bg-card border border-border rounded-lg p-6 elevation-1">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 space-y-4 sm:space-y-0">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Financial Details</h3>
          <p className="text-sm text-muted-foreground">Revenue streams and payout schedules</p>
        </div>
        
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <select 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Status</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="disputed">Disputed</option>
          </select>
          
          <Button variant="outline" size="sm" iconName="Download">
            Export
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-2 font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('date')}
                  className="flex items-center space-x-1 hover:text-foreground transition-hover"
                >
                  <span>Date</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left py-3 px-2 font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('type')}
                  className="flex items-center space-x-1 hover:text-foreground transition-hover"
                >
                  <span>Type</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-left py-3 px-2 font-medium text-muted-foreground">Partner</th>
              <th className="text-right py-3 px-2 font-medium text-muted-foreground">
                <button 
                  onClick={() => handleSort('amount')}
                  className="flex items-center space-x-1 hover:text-foreground transition-hover ml-auto"
                >
                  <span>Amount</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="text-center py-3 px-2 font-medium text-muted-foreground">Status</th>
              <th className="text-center py-3 px-2 font-medium text-muted-foreground">Disputes</th>
              <th className="text-center py-3 px-2 font-medium text-muted-foreground">Payout Date</th>
              <th className="text-center py-3 px-2 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedData?.map((item) => (
              <tr key={item?.id} className="border-b border-border hover:bg-muted/30 transition-hover">
                <td className="py-3 px-2 text-foreground">
                  {new Date(item.date)?.toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </td>
                <td className="py-3 px-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(item?.type)}`}>
                    {item?.type}
                  </span>
                </td>
                <td className="py-3 px-2">
                  <div>
                    <p className="text-foreground font-medium">{item?.partner}</p>
                    <p className="text-xs text-muted-foreground">{item?.orders} orders</p>
                  </div>
                </td>
                <td className="py-3 px-2 text-right font-semibold text-foreground">
                  ${item?.amount?.toLocaleString()}
                </td>
                <td className="py-3 px-2 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item?.status)}`}>
                    {item?.status}
                  </span>
                </td>
                <td className="py-3 px-2 text-center">
                  {item?.disputes > 0 ? (
                    <span className="flex items-center justify-center space-x-1 text-error">
                      <Icon name="AlertTriangle" size={14} />
                      <span>{item?.disputes}</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
                <td className="py-3 px-2 text-center text-muted-foreground">
                  {item?.payoutDate === 'On Hold' ? (
                    <span className="text-warning font-medium">On Hold</span>
                  ) : (
                    new Date(item.payoutDate)?.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric' 
                    })
                  )}
                </td>
                <td className="py-3 px-2 text-center">
                  <div className="flex items-center justify-center space-x-1">
                    <Button variant="ghost" size="sm" iconName="Eye" className="p-1" />
                    {item?.disputes > 0 && (
                      <Button variant="ghost" size="sm" iconName="MessageSquare" className="p-1 text-error" />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-6 pt-4 border-t border-border space-y-4 sm:space-y-0">
        <div className="text-sm text-muted-foreground">
          Showing {sortedData?.length} of {financialData?.length} transactions
        </div>
        
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" disabled>
            <Icon name="ChevronLeft" size={16} />
          </Button>
          <span className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded">1</span>
          <Button variant="outline" size="sm">
            <Icon name="ChevronRight" size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FinancialDetailsTable;